import { CURRENT_REPORTING_YEAR } from './mockData';

// Regulatory data-completeness gap engine for the free-tier mockup.
//
// Mirrors the real declarant-side dataGapSummary taxonomy
// (Suppliers/components/SupplierDataGapsCell.jsx) and the severity semantics
// from SupplierChaseHelpModal.jsx: high = missing SEFA / missing emissions,
// medium = default or incomplete data, low = unverified / nice-to-have.
//
// Gap shape:
//   { id, type, severity: 'high'|'medium'|'low', message,
//     scope: { installationId?, factorId? },
//     fix: { path } | { feature } }   // feature → premium-only fix (upsell)

const SEVERITY_WEIGHT = { high: 20, medium: 10, low: 5 };

export const SEVERITY_RANK = { high: 3, medium: 2, low: 1 };

// `labelFor` (Item 16B, fixpack 2026-07-23): this module is pure state code
// (no hooks, no CBAM-catalog fetch of its own), so it can't resolve a real
// CN-good label like useCnGoodLabel does — it takes one in from the React
// side that already has it (MockDataContext). Omitted/no-op callers (this
// module's own tests, any other caller that doesn't pass one) get the raw
// CN code, which is a plain, honest fallback rather than the demo-era
// cnCodeLabel's silently-wrong hardcoded 6-entry list.
export function computeGaps(data, { labelFor } = {}) {
  const label = typeof labelFor === 'function' ? labelFor : (code) => code;
  const gaps = [];
  const { company, installations, emissionFactors } = data;

  // Company contact incomplete (phone is required on the real operator form).
  if (!company.saved || !String(company.phoneNumber || '').trim()) {
    gaps.push({
      id: 'company-incomplete',
      type: 'company_incomplete',
      severity: 'medium',
      message: 'Company profile incomplete — declarants need a saved contact with a phone number.',
      scope: {},
      fix: { path: '/supplier/company' },
    });
  }

  installations.forEach((inst) => {
    if (!String(inst.cbamRegistryId || '').trim()) {
      gaps.push({
        id: `registry-${inst.id}`,
        type: 'missing_registry_id',
        severity: 'medium',
        message: `${inst.name}: no CBAM Registry ID — declarants need it to reference your installation.`,
        scope: { installationId: inst.id },
        fix: { path: '/supplier/installations' },
      });
    }
    if (!String(inst.latitude || '').trim() || !String(inst.longitude || '').trim()) {
      gaps.push({
        id: `coords-${inst.id}`,
        type: 'missing_coordinates',
        severity: 'low',
        message: `${inst.name}: missing coordinates of the main emissions source.`,
        scope: { installationId: inst.id },
        fix: { path: '/supplier/installations' },
      });
    }
    const factorCount = emissionFactors.filter((f) => f.installationId === inst.id).length;
    if (factorCount === 0) {
      gaps.push({
        id: `emissions-${inst.id}`,
        type: 'missing_emissions',
        severity: 'high',
        message: `${inst.name} has no emissions data — customers can't source CBAM figures from it.`,
        scope: { installationId: inst.id },
        fix: { path: '/supplier/emission-factors' },
      });
    }
  });

  // Stale reporting period: nothing covering the current year yet.
  if (
    emissionFactors.length > 0 &&
    !emissionFactors.some((f) => (f.periodFrom || '').startsWith(CURRENT_REPORTING_YEAR))
  ) {
    gaps.push({
      id: 'stale-period',
      type: 'stale_period',
      severity: 'medium',
      message: `No emissions data for ${CURRENT_REPORTING_YEAR} yet — declarants will request the current reporting period.`,
      scope: {},
      fix: { path: '/supplier/emission-factors' },
    });
  }

  emissionFactors.forEach((f) => {
    // No qualifying-parameters gap here: the canonical revision read model only
    // carries production-method CODES (`parameters` is always []), so the gap
    // would fire for every factor with no way for the supplier to resolve it.
    // Reinstate if the revision model ever exposes parameter records.
    if (Number(f.shareDefaultEmissions) > 0) {
      gaps.push({
        id: `defaults-${f.id}`,
        type: 'default_data',
        severity: 'medium',
        message: `${label(f.cnCode)}: ${f.shareDefaultEmissions}% default emissions instead of actual data.`,
        scope: { factorId: f.id, installationId: f.installationId },
        fix: { path: '/supplier/emission-factors' },
      });
    }
  });

  // R2 (S7 fix batch 3): `missing_sefa` deliberately DROPPED. It used to fire
  // unconditionally whenever any factors existed, but every published
  // revision now carries an `authoritativeSefa` — the backend publish
  // contract (`supplierFactorPublication.ts` `normalizeSefa`) requires and
  // validates a finite `total` SEFA on every revision (direct-entry,
  // correction, and template origins alike; template origin additionally
  // requires production+precursor), so a published factor can never be
  // missing it. The read model exposes it per-factor as
  // `authoritativeSefa` (supplierFactorReadModel.js) precisely so a
  // per-factor gap COULD be reinstated here if that contract ever changes —
  // there is currently no reachable case where it would fire.

  // Premium-only fixes — completeness and upsell fused. Aggregated across
  // records so they read as one actionable item each.
  if (emissionFactors.length > 0) {
    gaps.push({
      id: 'unverified',
      type: 'unverified',
      severity: 'low',
      message: `${emissionFactors.length} record${emissionFactors.length === 1 ? '' : 's'} unverified — verified data carries more weight with declarants.`,
      scope: {},
      fix: { feature: 'verification' },
    });
  }

  return gaps.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);
}

// Portal readiness: 100 minus severity-weighted gaps, floored at 0.
// Color thresholds follow SupplierDataScore: ≥80 green, ≥50 amber, else red.
export function computeReadiness(gaps) {
  const penalty = gaps.reduce((sum, g) => sum + (SEVERITY_WEIGHT[g.severity] || 0), 0);
  return Math.max(0, 100 - penalty);
}

export const readinessTone = (score) => {
  if (score >= 80) return { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' };
  if (score >= 50) return { bar: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50' };
  return { bar: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' };
};

export const highestSeverity = (gaps) =>
  gaps.reduce(
    (max, g) => (SEVERITY_RANK[g.severity] > SEVERITY_RANK[max] ? g.severity : max),
    'low',
  );
